import React from "react"

import { MapPin, Truck } from "lucide-react"
import { Marker } from "react-map-gl/mapbox"

import { LocationIdentity, MapCard } from "@/components/collections/monitoring/MapOverlayCard"
import { formatRouteCount } from "@/lib/mapOverlay"
import { getSlotTypePinClasses } from "@/lib/slotTypeDisplay"
import { cn } from "@/lib/utils"
import { Route, Stop } from "@/types/collections"

export interface LocationEntry {
  stop: Stop
  route: Route
}

interface LocationPinProps {
  lat: number
  lng: number
  entries: LocationEntry[]
  isHovered: boolean
  /** The click popup is open, so the hover card stays hidden. */
  isPopupOpen: boolean
  onHoverChange: (hovered: boolean) => void
  onSelect: () => void
}

const LocationPin: React.FC<LocationPinProps> = ({
  lat,
  lng,
  entries,
  isHovered,
  isPopupOpen,
  onHoverChange,
  onSelect,
}) => {
  const stop = entries[0].stop
  const slotType = stop.slotType ?? (stop as { stop_type?: string }).stop_type ?? ""
  const pinClassName = getSlotTypePinClasses(slotType)
  const servedOrSkipped = entries.every(
    (e) => e.stop.status === "completed" || e.stop.status === "skipped"
  )
  const routeCodes = [...new Set(entries.map((e) => e.route.code))]

  return (
    <Marker
      longitude={lng}
      latitude={lat}
      anchor="bottom"
      style={{ zIndex: isHovered ? 1000 : 0 }}
    >
      <div
        className={cn("group relative cursor-pointer", servedOrSkipped && "opacity-35")}
        onMouseEnter={() => onHoverChange(true)}
        onMouseLeave={() => onHoverChange(false)}
        onClick={(e) => {
          e.stopPropagation()
          onSelect()
        }}
      >
        <MapPin size={24} className={cn(pinClassName)} />
        {/* onMouseEnter/Leave don't fire on click, so without this the hover card stacks on the
            popup. */}
        {isHovered && !isPopupOpen && (
          <div className="pointer-events-none absolute bottom-full left-1/2 z-[70] mb-2 -translate-x-1/2">
            <MapCard>
              <div className="p-2.5">
                <LocationIdentity name={stop.name} address={stop.address} />
              </div>
              <div className="flex items-center gap-1.5 border-t border-palette-jungleGreen100 px-2.5 py-1.5 text-[10px] text-amber-700">
                <Truck size={10} className="flex-shrink-0 text-amber-600" />
                {routeCodes.length === 1 ? (
                  <span className="truncate" title={routeCodes[0]}>
                    {routeCodes[0]}
                  </span>
                ) : (
                  <span>{formatRouteCount(routeCodes.length)}</span>
                )}
                {servedOrSkipped && (
                  <span className="ml-auto flex-shrink-0 text-palette-jungleGreen400">
                    Served / skipped
                  </span>
                )}
              </div>
              <div className="bg-palette-jungleGreen000 px-2.5 py-1 text-[10px] text-palette-jungleGreen400">
                Click for detail
              </div>
            </MapCard>
          </div>
        )}
      </div>
    </Marker>
  )
}

export default LocationPin
